// Góc nhìn Nhân viên Telesales: việc cần làm hôm nay, chỉ tiêu cá nhân, danh sách khách theo mã S, lịch đã đặt
import React, { useMemo, useState } from 'react';
import { Select, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { CalendarPlus, ListTodo, PhoneCall, PhoneForwarded, Trophy, Wallet } from 'lucide-react';
import { AGENTS, DAY, branchOf, type Appt } from './data';
import {
  ARRIVED, STAGES, STAGE_LABEL, agentLive, agentPerf, apptStatus, callsIn, isLate, isOverdue, pct, pipeline, rangeOf, tasks, type Ctx, type Perf, type TaskKind,
} from './logic';
import useTeleDash from './store';
import TaskTable from './TaskTable';
import { AGENT_STATUS, APPT_STATUS, AgentName, Bar, Card, ConsultantName, Empty, Kpi, StageChip, dm, hm, money, stageColor } from './ui';

export default function AgentView({ ctx, canPick }: { ctx: Ctx; canPick: boolean }) {
  const { agentId, set, cfg } = useTeleDash();
  const [kind, setKind] = useState<TaskKind | 'all'>('all');
  const { now, m } = ctx;
  const today = dayjs(now).startOf('day').valueOf();

  const st = agentLive(agentId, ctx);
  const myTasks = useMemo(() => tasks(ctx, { agentId }), [ctx, agentId]);
  const late = myTasks.filter(t => isOverdue(t, now)).length;
  const out = callsIn(m, { from: today, to: now }).filter(cl => cl.agentId === agentId && cl.dir === 'out');
  const connected = out.filter(cl => cl.ok).length;
  const bookedToday = m.appts.filter(a => a.bookedBy === agentId && a.bookedAt >= today && a.bookedAt <= now).length;
  const month = useMemo(() => agentPerf(ctx, rangeOf('month', now)), [ctx, now]);
  const me = month.find(p => p.id === agentId) as Perf;
  const ranked = [...month].sort((a, b) => b.booked - a.booked || b.revenue - a.revenue);
  const rank = ranked.findIndex(p => p.id === agentId) + 1;
  const pipe = useMemo(() => pipeline(ctx, agentId), [ctx, agentId]);
  const pipeMax = Math.max(1, ...Object.values(pipe));
  const upcoming = m.appts.filter(a => a.bookedBy === agentId && a.at >= today && a.at < today + 2 * DAY && a.bookedAt <= now && apptStatus(a, ctx) !== 'cancelled');

  const apptCols: ColumnsType<Appt> = [
    { title: 'Giờ', key: 'at', width: 80, render: (_, a) => <div className="leading-tight"><b>{hm(a.at)}</b><div className="text-[11px] text-gray-500">{a.at >= today + DAY ? 'Ngày mai' : 'Hôm nay'}</div></div> },
    { title: 'Khách', key: 'l', render: (_, a) => { const l = m.leadById[a.leadId]; return <div className="leading-tight"><b className="text-gray-800 dark:text-gray-100">{l.name}</b><div className="text-[11px] text-gray-500">{l.phone} · {branchOf(a.branchId)?.name}</div></div>; } },
    {
      title: 'Trạng thái', key: 'st', render: (_, a) => {
        const s = apptStatus(a, ctx);
        return (
          <div className="leading-tight">
            <Tag color={APPT_STATUS[s].color} className="!m-0">{APPT_STATUS[s].label}</Tag>
            {isLate(a, ctx) && <div className="mt-0.5 text-[11px] font-semibold text-red-500">Quá giờ chưa đến</div>}
            {['consulting', 'won', 'deposit', 'no_need', 'medical'].includes(s) && <div className="mt-0.5 text-[11px] text-gray-500"><ConsultantName id={ctx.ov.appt[a.id]?.consultantId ?? a.consultantId} /></div>}
          </div>
        );
      },
    },
  ];

  const rankCols: ColumnsType<Perf> = [
    { title: '#', key: 'r', width: 36, render: (_, p) => <b className={ranked.indexOf(p) < 3 ? 'text-amber-500' : 'text-gray-400'}>{ranked.indexOf(p) + 1}</b> },
    { title: 'Nhân viên', key: 'n', render: (_, p) => <AgentName id={p.id} /> },
    { title: 'Lịch đặt', dataIndex: 'booked', align: 'right' },
    { title: 'Tỷ lệ đến', key: 's', align: 'right', render: (_, p) => (p.due ? `${pct(p.arrived, p.due)}%` : '—') },
    { title: 'Chốt', dataIndex: 'closed', align: 'right' },
    { title: 'Doanh số', dataIndex: 'revenue', align: 'right', render: v => money(v) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {canPick
          ? <Select className="w-64" value={agentId} onChange={v => set({ agentId: v })} options={AGENTS.map(a => ({ value: a.id, label: <AgentName id={a.id} /> }))} labelRender={({ value }) => <AgentName id={String(value)} />} />
          : <AgentName id={agentId} />}
        <Tag color={AGENT_STATUS[st.status].color} className="!m-0">{AGENT_STATUS[st.status].label}{st.reason ? ` · ${st.reason}` : ''}</Tag>
        <span className="text-xs text-gray-500">Hôm nay {dayjs(now).format('DD/MM')} · cập nhật {hm(now)}</span>
        {late > 0 && <span className="ml-auto rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600 dark:bg-red-500/15 dark:text-red-400">{late} việc đã trễ hạn — ưu tiên xử lý trước</span>}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Kpi icon={<ListTodo size={16} />} tone={late ? 'red' : 'teal'} label="Việc cần làm" value={myTasks.length} alert={late > 0} sub={late ? <span className="text-red-500">{late} việc trễ hạn</span> : 'Không có việc trễ'} onClick={() => setKind('all')} />
        <Kpi icon={<PhoneCall size={16} />} tone="teal" label="Cuộc gọi đi" value={<>{out.length}<span className="text-base text-gray-400"> / {cfg.callTarget}</span></>} sub={<Bar className="mt-1" value={out.length} max={cfg.callTarget} />} />
        <Kpi icon={<PhoneForwarded size={16} />} tone="sky" label="Kết nối" value={`${pct(connected, out.length)}%`} sub={`${connected} cuộc khách nghe máy`} />
        <Kpi icon={<CalendarPlus size={16} />} tone={bookedToday >= cfg.bookTarget ? 'green' : 'indigo'} label="Lịch đặt hôm nay" value={<>{bookedToday}<span className="text-base text-gray-400"> / {cfg.bookTarget}</span></>}
          sub={<Bar className="mt-1" value={bookedToday} max={cfg.bookTarget} color={bookedToday >= cfg.bookTarget ? '#16a34a' : '#6366f1'} />} />
        <Kpi icon={<Trophy size={16} />} tone="amber" label="Tháng này" value={`${me.booked} lịch`} sub={<>Khách đến {me.due ? `${pct(me.arrived, me.due)}%` : '—'} · hạng <b>{rank}</b>/{AGENTS.length}</>} />
        <Kpi icon={<Wallet size={16} />} tone="green" label="Doanh số tháng" value={money(me.revenue)} sub={`${me.closed} khách chốt / cọc`} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
        <Card title="Việc cần làm — sắp theo hạn xử lý">
          <TaskTable ctx={ctx} list={myTasks} kind={kind} onKind={setKind} height={460} />
          <p className="mt-2 text-xs text-gray-500">Gọi nhỡ phải gọi lại trong {cfg.missedMin} phút · khách mới trong {cfg.newLeadMin} phút · khách {Object.entries(cfg.forgotStages ?? { 'S2–S4': cfg.forgotDays }).map(([k, d]) => `${k} quá ${d} ngày`).join(', ')} không chạm là bị bỏ quên.</p>
        </Card>
        <div className="flex min-w-0 flex-col gap-4">
          <Card title="Khách của tôi theo mã S" extra={<span className="text-xs text-gray-500">khách vào 30 ngày</span>}>
            <div className="space-y-1.5">
              {STAGES.filter(s => pipe[s]).map(s => (
                <Tooltip key={s} title={STAGE_LABEL[s]} placement="left">
                  <div className="grid grid-cols-[44px_1fr_32px] items-center gap-2 text-[12px]">
                    <StageChip s={s} />
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"><div className="h-full rounded-full" style={{ width: `${(pipe[s] / pipeMax) * 100}%`, background: stageColor(s) }} /></div>
                    <b className="text-right text-gray-700 dark:text-gray-200">{pipe[s]}</b>
                  </div>
                </Tooltip>
              ))}
            </div>
          </Card>
          <Card title="Lịch tôi đã đặt — hôm nay & ngày mai" extra={<span className="text-xs text-gray-500">{upcoming.filter(a => ARRIVED.includes(apptStatus(a, ctx))).length}/{upcoming.length} đã đến</span>}>
            {upcoming.length
              ? <Table<Appt> size="small" rowKey="id" columns={apptCols} dataSource={upcoming} pagination={false} scroll={{ y: 300 }} />
              : <Empty text="Chưa có lịch nào" />}
          </Card>
        </div>
      </div>

      <Card title={`Xếp hạng tháng ${dayjs(now).format('MM/YYYY')}`} extra={<span className="text-xs text-gray-500">Theo số lịch đặt, sau đó doanh số · cập nhật {dm(now)}</span>}>
        <Table<Perf> size="small" rowKey="id" columns={rankCols} dataSource={ranked} pagination={false} scroll={{ x: 'max-content' }}
          rowClassName={p => (p.id === agentId ? 'bg-[#e8f4f7] dark:bg-[#3e91ab]/15' : '')} />
      </Card>
    </div>
  );
}
